import { useState } from 'react';
import { Alert, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useDecidePlan } from '@diggens/shared-hooks';
import type { PlanApproval } from '@diggens/types';
import { PlanSteps, formatWhen } from '@diggens/chat';

/** A plan waiting for an admin, with Approve and Reject; `onOpenRun` adds a link to the run that proposed it. */
export function PendingCard({ approval, now, onOpenRun }: { approval: PlanApproval; now: number; onOpenRun?: () => void }) {
  const [note, setNote] = useState('');
  const decide = useDecidePlan();
  const remaining = Date.parse(approval.expiresAt) - now;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderLeft: 6, borderLeftColor: 'warning.main' }}>
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography variant="h6">Approve your Chief of Staff’s plan{approval.revision > 1 ? ' (revised)' : ''}</Typography>
        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {remaining > 0 ? `Pending · expires ${formatWhen(approval.expiresAt)}` : 'Expired'}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ mt: 0.5 }}>
        {approval.goal}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Requested by {approval.requestedBy ?? 'a user'} at {formatWhen(approval.requestedAt)}. Your Chief of Staff carries the plan out as soon as you approve it, and steps marked “Changes data” only
        run if you approve.
      </Typography>
      <PlanSteps steps={approval.steps} showTasks />
      <TextField
        label="Note for your Chief of Staff (optional)"
        placeholder="Why you approved or rejected it"
        size="small"
        fullWidth
        value={note}
        onChange={(event) => setNote(event.target.value)}
        slotProps={{ htmlInput: { maxLength: 500 } }}
        sx={{ mt: 2 }}
      />
      {decide.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {decide.error.message}
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" disabled={decide.isPending || remaining <= 0} onClick={() => decide.mutate({ planId: approval.planId, decision: 'approve', note: note.trim() || undefined })}>
          Approve plan
        </Button>
        <Button variant="outlined" disabled={decide.isPending || remaining <= 0} onClick={() => decide.mutate({ planId: approval.planId, decision: 'reject', note: note.trim() || undefined })}>
          Reject plan
        </Button>
        {onOpenRun && <Button onClick={onOpenRun}>View run</Button>}
      </Stack>
    </Paper>
  );
}
